/**
 * Platform RevOps subscriptions API client.
 *
 * Talks to the real backend (/api/platform/v1/revops/*) instead of the old
 * localStorage store. Returns the RevOps module types (Subscription, PricingPlan,
 * Customer, MRRSummary) so it can feed the SubscriptionManager component directly.
 *
 * Auth pattern mirrors TierConfigurator.tsx (Bearer + X-CBO-ID + X-Tenant-ID).
 */

import type {
  Subscription,
  SubscriptionStatus,
  PricingPlan,
  Customer,
  MRRSummary,
  BillingInterval,
  PricingModel,
} from '@/src/platform/modules/revops/types';

const PLATFORM_BACKEND_URL =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_PLATFORM_API_URL) ||
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_WEAVEIN_API_URL) ||
  'https://weavein-api.onrender.com';

function getAuthHeaders(): Record<string, string> {
  const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null;
  const cboId = typeof window !== 'undefined' ? localStorage.getItem('active_cbo_id') : null;
  const tenantSlug = typeof window !== 'undefined' ? localStorage.getItem('platform_tenant_slug') : null;
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (cboId) headers['X-CBO-ID'] = cboId;
  if (tenantSlug) headers['X-Tenant-ID'] = tenantSlug;
  return headers;
}

async function api<T>(path: string, options: RequestInit = {}): Promise<T> {
  const res = await fetch(`${PLATFORM_BACKEND_URL}/api/platform/v1/revops${path}`, {
    ...options,
    headers: { ...getAuthHeaders(), ...options.headers },
  });
  if (!res.ok) {
    const detail = (await res.json().catch(() => ({})))?.detail;
    throw new Error(detail || `HTTP ${res.status}`);
  }
  return res.json();
}

// ---- mappers: backend snake_case rows -> module camelCase types --------------

const num = (v: unknown): number | null => (v === null || v === undefined ? null : Number(v));

/** Backend subscription statuses already match SubscriptionStatus except spelling of 'canceled'. */
function mapStatus(s: string | null): SubscriptionStatus {
  if (s === 'cancelled') return 'canceled';
  const allowed: SubscriptionStatus[] = ['trialing', 'active', 'past_due', 'paused', 'canceled', 'expired'];
  return (allowed.includes(s as SubscriptionStatus) ? s : 'active') as SubscriptionStatus;
}

function mapSubscription(r: any): Subscription {
  return {
    id: String(r.id),
    tenantId: r.tenant_id ? String(r.tenant_id) : '',
    customerId: r.customer_id ? String(r.customer_id) : '',
    subscriptionNumber: r.subscription_number || '',
    planId: r.plan_id ? String(r.plan_id) : '',
    planName: r.plan_name ?? null,
    quantity: r.quantity ?? 1,
    status: mapStatus(r.status),
    billingInterval: (r.billing_interval || 'monthly') as BillingInterval,
    billingIntervalCount: r.billing_interval_count ?? 1,
    billingDay: null,
    billingAnchor: null,
    currentPeriodStart: r.current_period_start || '',
    currentPeriodEnd: r.current_period_end || '',
    startDate: r.start_date || '',
    trialStart: r.trial_start ?? null,
    trialEnd: r.trial_end ?? null,
    canceledAt: r.canceled_at ?? null,
    cancelAtPeriodEnd: !!r.cancel_at_period_end,
    endedAt: null,
    pauseStart: null,
    pauseEnd: null,
    unitPrice: num(r.unit_price),
    currency: r.currency || 'USD',
    discountId: r.discount_id ? String(r.discount_id) : null,
    contractStart: r.contract_start ?? null,
    contractEnd: r.contract_end ?? null,
    autoRenew: r.auto_renew ?? true,
    mrr: num(r.mrr),
    arr: num(r.arr),
    defaultPaymentMethodId: null,
    opportunityId: r.opportunity_id ? String(r.opportunity_id) : null,
    createdByUserId: null,
    metadata: r.metadata || {},
    createdAt: r.created_at || '',
    updatedAt: r.updated_at || r.created_at || '',
  };
}

/** A plan tier row (from GET /plan-tiers) mapped to a PricingPlan for display. */
function mapTierToPlan(t: any): PricingPlan {
  return {
    id: String(t.id),
    tenantId: '',
    productId: t.product_id ? String(t.product_id) : '',
    planCode: t.tier_code || '',
    planName: t.tier_name || '',
    description: t.description ?? null,
    pricingModel: (t.pricing_model || 'per_seat') as PricingModel,
    billingInterval: (t.billing_interval || 'monthly') as BillingInterval,
    billingIntervalCount: 1,
    basePrice: num(t.base_monthly_price),
    currency: 'USD',
    unitPrice: num(t.per_user_price),
    unitName: 'user',
    includedUnits: 0,
    minUnits: t.min_users ?? 1,
    maxUnits: t.max_users ?? null,
    pricingTiers: [],
    setupFee: Number(t.setup_fee || 0),
    trialDays: t.trial_days ?? 0,
    trialRequiresPayment: false,
    minContractMonths: t.min_contract_months ?? 0,
    autoRenew: true,
    isActive: t.is_active ?? true,
    isPublic: true,
    availableFrom: null,
    availableUntil: null,
    features: [],
    metadata: {},
    createdAt: t.created_at || '',
    updatedAt: t.updated_at || '',
  };
}

// ---- API surface -------------------------------------------------------------

export async function listSubscriptions(params?: { status?: string }): Promise<Subscription[]> {
  const q = params?.status && params.status !== 'all' ? `?status=${encodeURIComponent(params.status)}` : '';
  const data = await api<{ subscriptions: any[] }>(`/subscriptions${q}`);
  return (data.subscriptions || []).map(mapSubscription);
}

export async function listPlanTiers(): Promise<PricingPlan[]> {
  const data = await api<{ plan_tiers: any[] }>(`/plan-tiers`);
  return (data.plan_tiers || []).map(mapTierToPlan);
}

export async function listCustomers(): Promise<Customer[]> {
  const data = await api<{ customers: any[] }>(`/customers`);
  return (data.customers || []).map((c: any) => ({
    id: String(c.id),
    tenantId: c.tenant_id ? String(c.tenant_id) : '',
    corporateAccountId: null,
    customerCode: c.customer_code ?? null,
    companyName: c.company_name || '',
    billingEmail: c.billing_email || '',
    billingName: c.billing_name ?? null,
    billingPhone: null,
    billingAddressLine1: null,
    billingAddressLine2: null,
    billingCity: null,
    billingState: null,
    billingPostalCode: null,
    billingCountry: 'US',
    taxId: null,
    taxExempt: false,
    taxExemptReason: null,
    defaultPaymentMethodId: null,
    paymentTerms: 'net_30' as Customer['paymentTerms'],
    autoCollection: true,
    currency: c.currency || 'USD',
    status: (c.status || 'active') as Customer['status'],
    stripeCustomerId: c.stripe_customer_id ?? null,
    creditBalance: Number(c.credit_balance || 0),
    metadata: c.metadata || {},
    createdAt: c.created_at || '',
    updatedAt: c.updated_at || '',
  }));
}

/** Server-side monthly cost for a tier at a given seat count (subtotal = monthly MRR). */
export async function calculateTier(tierId: string, userCount: number): Promise<{ subtotal: number; setup_fee: number }> {
  return api(`/plan-tiers/${tierId}/calculate`, {
    method: 'POST',
    body: JSON.stringify({ user_count: userCount }),
  });
}

export async function createSubscription(payload: {
  cbo_id?: number;
  customer_id?: string;
  plan_tier_id: string;
  quantity?: number;
  opportunity_id?: string;
}): Promise<{ id: string; subscription_number: string; mrr: number }> {
  return api(`/subscriptions`, { method: 'POST', body: JSON.stringify(payload) });
}

export async function changePlan(subscriptionId: string, planTierId: string, quantity?: number): Promise<any> {
  return api(`/subscriptions/${subscriptionId}/change-plan`, {
    method: 'POST',
    body: JSON.stringify({ plan_tier_id: planTierId, quantity }),
  });
}

export async function cancelSubscription(subscriptionId: string): Promise<any> {
  return api(`/subscriptions/${subscriptionId}/cancel`, { method: 'POST', body: JSON.stringify({}) });
}

export async function pauseSubscription(subscriptionId: string): Promise<any> {
  return api(`/subscriptions/${subscriptionId}/pause`, { method: 'POST', body: JSON.stringify({}) });
}

export async function resumeSubscription(subscriptionId: string): Promise<any> {
  return api(`/subscriptions/${subscriptionId}/resume`, { method: 'POST', body: JSON.stringify({}) });
}

/** Derive the SubscriptionManager MRRSummary from a subscription list (no dedicated endpoint). */
export function deriveMrrSummary(subs: Subscription[], customerCount: number): MRRSummary {
  const active = subs.filter((s) => s.status === 'active' || s.status === 'trialing');
  const totalMrr = active.reduce((sum, s) => sum + (s.mrr || 0), 0);
  return {
    totalMrr,
    totalArr: totalMrr * 12,
    totalCustomers: customerCount,
    totalSubscriptions: active.length,
    newMrr: 0,
    expansionMrr: 0,
    contractionMrr: 0,
    churnMrr: 0,
    reactivationMrr: 0,
    netMrrChange: 0,
  };
}
